export interface ScamPreset {
  id: string;
  title: string;
  category: 'SMS' | 'Email' | 'Job' | 'Marketplace' | 'Invoice' | 'Social';
  channel: 'sms' | 'email' | 'message' | 'job_offer' | 'invoice' | 'marketplace' | 'social_media';
  previewText: string;
  fullContent: string;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  riskScore: number;
  explanation: string;
  impersonating?: string;
  indicators: {
    urgency: boolean;
    suspiciousPayment: boolean;
    suspiciousLink: boolean;
    senderMismatch: boolean;
    sensitiveInfoRequest: boolean;
  };
  actions: string[];
}

export const SCAM_PRESETS: ScamPreset[] = [
  {
    id: 'auspost-sms',
    title: 'AusPost Failed Delivery SMS',
    category: 'SMS',
    channel: 'sms',
    previewText: 'AusPost: Your package #AU82910 could not be delivered due to an unpaid duty fee ($2.35)...',
    fullContent: `AusPost: Your parcel AU-88219-X has been held at our Sydney distribution centre due to an incomplete delivery address and an unpaid handling fee ($2.35). Please update your details and pay the surcharge within 24 hours to avoid package return: https://auspost-redelivery-express-au.net/update`,
    riskLevel: 'CRITICAL',
    riskScore: 96,
    impersonating: 'Australia Post',
    explanation: 'High-volume phishing scam impersonating Australia Post. Uses artificial urgency (24 hours), requests credit card details for a tiny fee ($2.35) to capture card numbers, and uses a fraudulent non-official domain (.net domain instead of auspost.com.au).',
    indicators: {
      urgency: true,
      suspiciousPayment: true,
      suspiciousLink: true,
      senderMismatch: true,
      sensitiveInfoRequest: true,
    },
    actions: [
      'Do not click the link or provide card details.',
      'Delete and block the sender number.',
      'Verify tracking numbers directly in the official Australia Post app.',
      'Report the SMS to Scamwatch (scamwatch.gov.au) or forward to 7726 (SPAM).'
    ]
  },
  {
    id: 'linkt-toll-sms',
    title: 'Linkt Toll Road Overdue Notice',
    category: 'SMS',
    channel: 'sms',
    previewText: 'LINKT: You have an outstanding toll invoice of $12.40. Avoid $180 fine by paying before 5PM...',
    fullContent: `LINKT Alert: Our records indicate an unpaid toll trip on the M2 Motorway. Overdue balance: $12.40. An administrative penalty fee of $184.00 will be issued if unpaid today. Clear your overdue notice immediately at: https://linkt-toll-settlement.com/pay`,
    riskLevel: 'CRITICAL',
    riskScore: 98,
    impersonating: 'Linkt / Transurban',
    explanation: 'Classic toll road phishing campaign targeting Australian drivers. Manufactures sudden financial anxiety by threatening heavy fines ($184), driving victims to a credential harvesting site.',
    indicators: {
      urgency: true,
      suspiciousPayment: true,
      suspiciousLink: true,
      senderMismatch: true,
      sensitiveInfoRequest: true,
    },
    actions: [
      'Do not click the link.',
      'Check your actual toll account by logging into the official Linkt app or website (linkt.com.au).',
      'Forward the message to 7726 to alert telecommunication providers.'
    ]
  },
  {
    id: 'mygov-ato-refund',
    title: 'myGov / ATO Tax Refund Notification',
    category: 'Email',
    channel: 'email',
    previewText: 'Australian Taxation Office: You have a pending tax return refund of $1,420.50 waiting...',
    fullContent: `From: ATO Notification Services <tax-refunds-alert@mygov-gov-au.org>
Subject: Important Notification: Outstanding Tax Refund #ATO-992140

Dear Australian Taxpayer,

The Australian Taxation Office (ATO) has finalized the recalculation of your 2025/2026 fiscal tax submission. You are entitled to a pending refund credit of $1,420.50 AUD.

Due to outdated banking details on file, your direct transfer failed.

To claim your refund into your preferred bank account, access the secure myGov portal below and verify your Identity & Credit Card details:
[Click Here to Claim Your Refund via myGov] (https://mygov-portal-secure-identity.com/claim)

Failure to claim within 48 hours will result in the funds being forfeited.

Regards,
Australian Taxation Office (ATO) & Services Australia`,
    riskLevel: 'CRITICAL',
    riskScore: 99,
    impersonating: 'Australian Taxation Office (ATO) & myGov',
    explanation: 'Dangerous impersonation of Australian government agencies. ATO NEVER sends emails asking you to click a link to claim refunds or provide credit card numbers. Legitimate tax refunds are paid directly into registered bank accounts via myGov.',
    indicators: {
      urgency: true,
      suspiciousPayment: false,
      suspiciousLink: true,
      senderMismatch: true,
      sensitiveInfoRequest: true,
    },
    actions: [
      'Do NOT click any links or enter credentials.',
      'Log into your official myGov account directly at my.gov.au to check your ATO inbox.',
      'Forward the email to ReportEmailFraud@ato.gov.au.',
      'Change your myGov password immediately if you entered any information.'
    ]
  },
  {
    id: 'marketplace-payid',
    title: 'Facebook Marketplace PayID Upgrade Scam',
    category: 'Marketplace',
    channel: 'marketplace',
    previewText: 'Buyer: I will pay via PayID right now, but my business account requires you to deposit $300...',
    fullContent: `Buyer: "Hi, is this still available? I'll take it for the asking price ($250). I am currently away for work in Newcastle so my courier will pick it up tomorrow. Can I have your PayID email to transfer the funds immediately?"

[Follow up message after you send email]:
"I just sent the payment via PayID, but I received an email saying your PayID account is not a Business Tier Account. PayID limits non-business transfers to $100. To upgrade your PayID account and release the $250, I will send an extra $300. You must immediately transfer the $300 back to the PayID validation agent so the full $550 unlocks in your account."`,
    riskLevel: 'HIGH',
    riskScore: 92,
    impersonating: 'PayID / Australian Payments Plus',
    explanation: 'Widespread Australian marketplace fraud. PayID is a free payment identifier and NEVER requires account upgrades, tier fees, or "security refunds". The buyer will send a fake email pretending to be PayID.',
    indicators: {
      urgency: true,
      suspiciousPayment: true,
      suspiciousLink: false,
      senderMismatch: true,
      sensitiveInfoRequest: true,
    },
    actions: [
      'Do NOT send any money back. The buyer has not sent real funds.',
      'Block the buyer and report their profile on Facebook / Gumtree.',
      'Insist on cash on pickup or in-app payment.'
    ]
  },
  {
    id: 'whatsapp-hi-mum',
    title: 'WhatsApp "Hi Mum / Dad" Impersonation',
    category: 'Social',
    channel: 'message',
    previewText: 'Unknown Number: Hi Mum, I dropped my phone in the sink and this is my temporary number...',
    fullContent: `Unknown Number: "Hi Mum, I dropped my phone down the toilet and broke the screen, so this is my new temporary number until my replacement arrives. Can you save this number? Also I’m in a real panic because I have an urgent bill due today and my bank app isn't set up on this loaner phone. Could you please transfer $1,850 to this account for me? BSB: 062-000 Acc: 1829 4819. I'll pay you back first thing tomorrow morning!"`,
    riskLevel: 'HIGH',
    riskScore: 95,
    impersonating: 'Family Member / Child',
    explanation: 'Emotional extortion scam targeting parents. Scammers prey on parental worry to elicit quick, unverified bank transfers before the victim can contact their child on their genuine phone or through other channels.',
    indicators: {
      urgency: true,
      suspiciousPayment: true,
      suspiciousLink: false,
      senderMismatch: true,
      sensitiveInfoRequest: false,
    },
    actions: [
      'Call your family member on their original known phone number or speak with them directly.',
      'Ask a personal question only your real family member would know.',
      'Never transfer money to an unknown bank account based solely on text messages.'
    ]
  },
  {
    id: 'legitimate-commbank-otp',
    title: 'Legitimate Bank NetCode / Security SMS',
    category: 'SMS',
    channel: 'sms',
    previewText: 'CommBank: 492810 is your NetCode. Do NOT share this code with anyone, including bank staff...',
    fullContent: `CommBank: 492810 is your NetCode for an online card purchase of $49.00 at JB HI-FI. If you did not make this transaction, call 13 2221 immediately. CommBank staff will NEVER ask for this code.`,
    riskLevel: 'LOW',
    riskScore: 12,
    impersonating: 'Commonwealth Bank of Australia (Legitimate)',
    explanation: 'This appears to be a legitimate one-time security NetCode SMS. Notice that it does NOT contain suspicious external links, warns you NEVER to disclose the code, and provides official phone contact for fraud reporting.',
    indicators: {
      urgency: false,
      suspiciousPayment: false,
      suspiciousLink: false,
      senderMismatch: false,
      sensitiveInfoRequest: false,
    },
    actions: [
      'If you initiated this purchase at JB Hi-Fi, enter the code on the merchant checkout screen.',
      'If you DID NOT initiate this purchase, someone is trying to use your card—call CommBank immediately on 13 2221.'
    ]
  }
];
